import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './ResetPasswordView.css';
import TopBar from '../../common/TopBar/TopBar';
import AuthInput from '../../buttons/AuthInput';
import CTAButton from '../../buttons/CTAButton';
import { resetPassword } from '../../../api/auth';

const ResetPasswordView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [token, setToken] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(true);
  const [isConfirmPasswordValid, setIsConfirmPasswordValid] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      setError('Invalid or missing reset token');
    }
  }, [location]);

  useEffect(() => {
    setIsPasswordValid(newPassword.length >= 8);
  }, [newPassword]);

  useEffect(() => {
    setIsConfirmPasswordValid(newPassword === confirmPassword);
  }, [newPassword, confirmPassword]);

  const isFormValid = isPasswordValid && isConfirmPasswordValid && newPassword.length > 0 && confirmPassword.length > 0;

  const handleResetPassword = async () => {
    if (isFormValid && !isResetting) {
      setIsResetting(true);
      try {
        await resetPassword(token, newPassword);
        navigate('/signin', { state: { message: 'Password reset successfully. Please sign in with your new password.' } });
      } catch (error) {
        setError('Failed to reset password. Please try again.');
        console.error('Password reset failed:', error);
      } finally {
        setIsResetting(false);
      }
    }
  };

  const togglePasswordVisibility = () => {
    setShowPasswords(!showPasswords);
  };

  return (
    <div className="reset-password">
      <TopBar showBackButton={true} />
      <h1 className="title">Reset Password</h1>
      <p className="description">
        Enter your new password below to reset your account password.
      </p>
      <div className="input-container">
        <AuthInput
          type={showPasswords ? "text" : "password"}
          placeholder="New password"
          value={newPassword}
          onChange={setNewPassword}
          isValid={isPasswordValid}
          showPassword={showPasswords}
          onTogglePassword={togglePasswordVisibility}
        />
        <AuthInput
          type={showPasswords ? "text" : "password"}
          placeholder="Confirm new password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          isValid={isConfirmPasswordValid}
          showPassword={showPasswords}
          onTogglePassword={togglePasswordVisibility}
        />
      </div>
      {error && <p className="error-message">{error}</p>}
      <CTAButton
        text={isResetting ? "Please wait..." : "Reset Password"}
        onClick={handleResetPassword}
        isActive={isFormValid && !isResetting}
      />
    </div>
  );
};

export default ResetPasswordView;
